import { Fragment, PureComponent, ReactNode } from 'react';
import { CERTIFICATES_CONFIG, CUSTOM_CERT_ID, EKA_VARIABLE_CLASSLIST } from './medicalCertificates';
import { getPtFullNameWithSalutation, Patient } from '../../store/types/patient';
import { getAgeString, randomString } from '../../store/utils';
import moment from 'moment';
import { Checkbox, Input, notification } from 'antd';
import { getDoc } from '../../store/idbHandler';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

type Props = {
    patient: Patient;
    certId: string;
    onContinue: (
        content: string,
        originalContent: string,
        heading: string,
        withPrintRxConfig: boolean,
        params?: { [k: string]: string },
    ) => void;
    customContent?: string;
    defaultHeading?: string;
    isMarkdown?: boolean;
};

class CreateCr extends PureComponent<Props> {
    EDITOR = 'EDITOR';
    selectionHandled = false;
    today = moment(new Date()).format('YYYY-MM-DD');
    ref: Input | undefined | null = null;
    VARIABLES_LABEL: { [k: string]: string } = {
        date: 'Custom date',
        today: `Today's date ${moment().format('DD-MM-YYYY')}`,
        input: 'Add Input',
        ptSign: "Patient's signature",
        docSign: "Doctor's signature",
        ptPhone: "Patient's phone number",
    };

    withPrintRxConfig = false;

    initEditor = (): void => {
        const doc = getDoc();
        const { certId, patient } = this.props;
        const variables: { [k: string]: string } = {
            ptName: getPtFullNameWithSalutation(patient),
            drName: `Dr. ${[doc?.profile.personal.name.f, doc?.profile.personal.name.l].join(' ')}`,
            ptAge: getAgeString({
                age: patient.personal.age?.value,
                month: patient.personal.age?.month,
                dob: patient.personal.age?.dob,
            }),
            ptPhone: patient.personal.phone?.n || '',
            ptSex: patient.personal.gender?.toUpperCase(),
            today: `<strong>${moment().format('DD-MM-YYYY')}</strong>`,
            date: `<strong class="ekavariable todayDate date"><input type="date" value="${this.today}" /></strong>`,
            input: `<strong class="ekavariable input"><input /></strong>`,
            ptSign: `<strong class="ekavariable input"><input variable="ptSign" disabled="true" value="<Signed by patient>" /></strong>`,
            docSign: `<strong class=""><img
            src="${doc?.profile?.professional?.signature}"
            style="max-width: 92px; height: 60px; object-fit: contain;"
        /> </strong>`,
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        tinymce?.init({
            selector: '#' + this.EDITOR,
            height: '100%',
            spellchecker_language: 'en',
            browser_spellcheck: true,
            elementpath: false,
            menubar: false,
            toolbar: `undo redo | styleselect | bold italic bullist| variables | table | alignleft aligncenter alignright alignjustify`,
            plugins: ['noneditable', 'template', 'table', 'advlist', 'lists'].filter(Boolean),
            template_replace_values: variables,
            extended_valid_elements: 'input[variable|disabled|value|type]',
            content_style:
                '.ekavariable { border-style: solid; border-width: 1px; border-radius: 4px; border-color: rgba(228, 229, 237); background-color: rgba(244, 246, 249)  } p { line-height: 32px; } input[variable="ptSign"] { color: transparent; height: 56px; background-color: white;}',
            // TODO add types
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setup: (editor) => {
                editor.ui.registry.addMenuButton('variables', {
                    type: 'menubutton',
                    text: 'Insert',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    fetch: (callback) => {
                        const buttons = Object.keys(variables)
                            .map(
                                (key) =>
                                    variables[key] && {
                                        type: 'menuitem',
                                        text: this.VARIABLES_LABEL[key] || variables[key],
                                        onAction: function () {
                                            editor.insertContent(
                                                `<span class="${
                                                    key === 'docSign' ? '' : EKA_VARIABLE_CLASSLIST
                                                } ${key}">${
                                                    variables[key]
                                                }</span><span class="space"> &nbsp;</span>`,
                                            );
                                        },
                                    },
                            )
                            .filter(Boolean);
                        callback(buttons);
                    },
                });

                editor.ui.registry.addButton('Date', {
                    text: 'Date',
                    onAction: function () {
                        editor.insertContent(
                            `<span class="ekavariable ${randomString(
                                4,
                            )} date"><input type="date" value="${
                                this.today
                            }"/></span> <span class="space">&nbsp;</span>`,
                        );
                    },
                });

                const handleSection = () => {
                    const sel = editor.selection.getSel();

                    if (sel.anchorOffset === sel.focusOffset) {
                        this.selectionHandled = false;
                        return;
                    }
                    if (this.selectionHandled) {
                        return;
                    }

                    const node = editor.selection.getNode();
                    if (node && Array.from(node.classList).includes(EKA_VARIABLE_CLASSLIST)) {
                        if (sel.anchorOffset !== sel.focusOffset) {
                            editor.selection.select(node);
                            this.selectionHandled = true;
                        }
                    }
                };

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                editor.on('mouseup', (e) => {
                    this.selectionHandled = false;
                    handleSection();
                });

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                editor.on('SelectionChange', (e) => {
                    handleSection();
                });

                editor.on('init', () => {
                    if (this.props.certId === CUSTOM_CERT_ID && this.props.customContent) {
                        editor.insertContent(this.props.customContent);
                    }
                });
            },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            init_instance_callback: (instance) => {
                if (CERTIFICATES_CONFIG[certId]?.template) {
                    instance.execCommand(
                        'mceInsertTemplate',
                        false,
                        CERTIFICATES_CONFIG[certId]?.template,
                    );
                }
            },
        });
    };

    componentDidMount = async (): Promise<void> => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (tinymce) {
            this.initEditor();
        }
    };

    componentWillUnmount = async (): Promise<void> => {
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            tinymce?.activeEditor?.destroy();
        } catch (e) {
            console.error(e);
        }
    };

    onContinue = (): void => {
        const { onContinue } = this.props;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const iframe: HTMLIFrameElement | null = document.getElementById('EDITOR_ifr');
        if (!iframe || !iframe.contentWindow) {
            // TODO handle error
            return;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const originalContent = tinymce?.activeEditor.getContent();
        const params: { [k: string]: string } = {};
        Array.from(iframe.contentWindow?.document.querySelectorAll('input')).forEach((input) => {
            const variable = input.getAttribute('variable') || '';
            params[variable] = input.value;

            if (variable === 'ptSign') {
                return;
            }
            if (input.parentElement) {
                input.parentElement.innerHTML =
                    input.type === 'date' ? moment(input.value).format('DD-MM-YYYY') : input.value;
            }
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const content = tinymce?.activeEditor.getContent();
        const heading = this.ref?.state?.value;

        if (!content || !originalContent || !heading) {
            notification.error({
                message: 'Please fill in the form',
                duration: 5,
            });
            return;
        }

        onContinue(content, originalContent, heading, this.withPrintRxConfig, params);
    };

    onWithPrintRxConfigChange = (e: CheckboxChangeEvent): void => {
        this.withPrintRxConfig = e.target.checked;
    };

    render(): ReactNode {
        const { certId, defaultHeading } = this.props;
        return (
            <Fragment>
                <div className="p-16 flex-1 overflow-auto space-y-16">
                    <div>
                        {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore */}
                        <Input
                            addonBefore="Heading"
                            ref={(ref) => (this.ref = ref)}
                            defaultValue={defaultHeading || CERTIFICATES_CONFIG[certId]?.heading}
                        />
                    </div>
                    <textarea id={this.EDITOR}></textarea>
                </div>
                <div className="p-16 flex justify-end bg-darwin-neutral-50 items-center space-x-16 select-none">
                    <Checkbox onChange={this.onWithPrintRxConfigChange}>
                        Use my rx template
                    </Checkbox>
                    <button
                        className="primary-btn  px-12 py-8 btn btn--small space-x-4"
                        onClick={this.onContinue}
                    >
                        Continue
                    </button>
                </div>
            </Fragment>
        );
    }
}

export default CreateCr;
